// ParcelFirmAssignment — the consultant FIRM engaged on a parcel, for the modal's value year.
//
// Replaces the old free-text `parcels.consultant_firm` input. That column still exists (the Siger
// importer lands strings in it) but this UI no longer writes it: the assignment is a real row in
// consultant_assignments with an FK to consultants.id, so a firm rename can no longer orphan it.
//
// A change from a year is a NEW ROW effective from that year; other years' rows are left byte-identical. One row per
// year (unique parcel_id, value_year). An existing year's row can be CORRECTED — its firm changed — or REMOVED, so
// the year follows the one before it (Philip 2026-10-28: La Guardia's 2021 read Admin Only - NTRG and could not be
// fixed). Both touch only that row: cross-year isolation forbids moving a row's year, not editing its firm, and each
// write checks its row count (an RLS-blocked write returns 0 rows and no error).
import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { resolveFirmAssignment, listFirmAssignments } from '../utils/resolveAssignment';

export default function ParcelFirmAssignment({ parcelId, valueYear, staffId, s, c }) {
  const [resolved, setResolved] = useState(null);   // { row, year } | null
  const [timeline, setTimeline] = useState([]);
  const [firms, setFirms]       = useState([]);
  const [open, setOpen]         = useState(false);  // change panel
  const [pickFirm, setPickFirm] = useState('');
  const [pickYear, setPickYear] = useState(String(Number(valueYear) + 1));
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState('');
  const [editId, setEditId]     = useState(null);   // the year row being changed
  const [editFirm, setEditFirm] = useState('');
  const [delId, setDelId]       = useState(null);   // the year row pending a confirmed remove

  const load = useCallback(async () => {
    if (!parcelId) return;
    setErr('');
    try {
      const [r, t] = await Promise.all([resolveFirmAssignment(parcelId, valueYear), listFirmAssignments(parcelId)]);
      setResolved(r); setTimeline(t);
    } catch (e) { setErr(e.message || 'Could not load the consultant assignment.'); }
  }, [parcelId, valueYear]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPickYear(String(Number(valueYear) + 1)); }, [valueYear]);
  useEffect(() => {
    supabase.from('consultants').select('id, company').order('company')
      .then(({ data }) => setFirms(data || []));
  }, []);

  async function save() {
    setBusy(true); setErr('');
    // consultant_id '' → null: the explicit "engagement ended" tombstone, not a missing pick.
    const { error } = await supabase.from('consultant_assignments').insert({
      parcel_id: parcelId, consultant_id: pickFirm || null,
      value_year: Number(pickYear), created_by: staffId || null,
    });
    setBusy(false);
    if (error) {
      // 23505 = unique (parcel_id, value_year). Say what to do, not what the constraint is called.
      setErr(error.code === '23505'
        ? `There is already an assignment starting ${pickYear} — edit that one instead of adding a second.`
        : (error.message || 'Could not save the assignment.'));
      return;
    }
    setOpen(false); setPickFirm(''); await load();
  }

  // Correct one year's firm ('' → the "engagement ended" tombstone, as in the change panel).
  async function saveEdit(t) {
    setBusy(true); setErr('');
    const { data, error } = await supabase.from('consultant_assignments')
      .update({ consultant_id: editFirm || null, updated_at: new Date().toISOString() }).eq('id', t.id).select('id');
    setBusy(false);
    if (error || (data || []).length !== 1) { setErr(error?.message || `Could not change the ${t.value_year} assignment.`); return; }
    setEditId(null); await load();
  }
  // Remove one year's row: that year then follows the latest earlier assignment, or none.
  async function remove(t) {
    setBusy(true); setErr('');
    const { data, error } = await supabase.from('consultant_assignments').delete().eq('id', t.id).select('id');
    setBusy(false);
    if (error || (data || []).length !== 1) { setErr(error?.message || `Could not remove the ${t.value_year} assignment.`); return; }
    setDelId(null); await load();
  }
  const firmName = (t) => (t.consultant_id ? (t.consultant?.company || '—') : 'none');

  const row = resolved?.row;
  const label = !row ? 'None on file'
    : row.consultant_id ? (row.consultant?.company || '(unknown firm)')
    : 'None — engagement ended';
  // The resolved year differs from the viewed year whenever the assignment started earlier. Saying so
  // stops "why does 2027 show a 2021 firm" being read as a bug.
  const inherited = row && row.value_year !== Number(valueYear);

  return (
    <div>
      <div style={s.label}>Consultant</div>
      <div style={{ fontSize: '13px', color: row?.consultant_id ? c.text : c.muted, fontWeight: row?.consultant_id ? 600 : 400 }}>
        {label}
      </div>
      {inherited && <div style={{ fontSize: '10px', color: c.muted, marginTop: '2px' }}>since {row.value_year}</div>}
      {err && <div style={{ fontSize: '11px', color: '#f87171', marginTop: '4px' }}>{err}</div>}

      <button onClick={() => setOpen(o => !o)}
        style={{ ...s.btnGhost, fontSize: '11px', padding: '3px 8px', marginTop: '6px' }}>
        {open ? 'Cancel' : 'Change consultant'}
      </button>

      {open && (
        <div style={{ marginTop: '8px', padding: '10px', border: `1px solid ${c.border}`, borderRadius: '8px' }}>
          <div style={{ fontSize: '10px', color: c.muted, marginBottom: '6px', fontStyle: 'italic' }}>
            Adds a new assignment from the year you choose. Earlier years keep the firm they already have.
          </div>
          <select value={pickFirm} onChange={e => setPickFirm(e.target.value)} style={{ ...s.input, marginBottom: '6px' }}>
            <option value="">— None (engagement ends) —</option>
            {firms.map(f => <option key={f.id} value={f.id}>{f.company}</option>)}
          </select>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: c.muted }}>Effective</span>
            <input type="number" value={pickYear} onChange={e => setPickYear(e.target.value)} style={{ ...s.input, width: '90px' }} />
            <button onClick={save} disabled={busy} style={{ ...s.btn, fontSize: '11px', padding: '4px 10px', opacity: busy ? 0.6 : 1 }}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {timeline.length > 0 && (
        <div style={{ marginTop: '8px', fontSize: '10px', color: c.muted }}>
          {timeline.map(t => {
            const linkBtn = { background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: '10px', cursor: 'pointer', textDecoration: 'underline' };
            // What the year falls back to once its row is gone: the latest earlier row (the timeline is newest first).
            const before = timeline.find(x => x.value_year < t.value_year);
            return (
              <div key={t.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 8px', marginTop: '2px' }}>
                {editId === t.id ? (<>
                  <span>{t.value_year} →</span>
                  <select value={editFirm} onChange={e => setEditFirm(e.target.value)} style={{ ...s.input, fontSize: '11px', padding: '2px 6px', width: 'auto' }}>
                    <option value="">— None (engagement ends) —</option>
                    {firms.map(f => <option key={f.id} value={f.id}>{f.company}</option>)}
                  </select>
                  <button onClick={() => saveEdit(t)} disabled={busy} style={{ ...s.btn, fontSize: '10px', padding: '2px 8px', opacity: busy ? 0.6 : 1 }}>Save</button>
                  <button onClick={() => setEditId(null)} style={{ ...linkBtn, color: c.muted }}>Cancel</button>
                </>) : delId === t.id ? (<>
                  <span>Remove {t.value_year} ({firmName(t)})? {t.value_year} will follow {before ? `${before.value_year} (${firmName(before)})` : 'no assignment'}.</span>
                  <button onClick={() => remove(t)} disabled={busy} style={{ ...s.btn, fontSize: '10px', padding: '2px 8px', opacity: busy ? 0.6 : 1 }}>Remove</button>
                  <button onClick={() => setDelId(null)} style={{ ...linkBtn, color: c.muted }}>Cancel</button>
                </>) : (<>
                  <span>{t.value_year} → {firmName(t)}</span>
                  <button onClick={() => { setDelId(null); setEditId(t.id); setEditFirm(t.consultant_id || ''); }} style={{ ...linkBtn, color: c.subtext || c.muted }}>change</button>
                  <button onClick={() => { setEditId(null); setDelId(t.id); }} style={{ ...linkBtn, color: '#f87171' }}>remove</button>
                </>)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
